import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import * as Icon from 'react-feather';
import { AvInput } from 'availity-reactstrap-validation';
import { Alert } from 'reactstrap';
import Marquee from '../../helpers/marquee.component';

/**
 * The OptionsList component.
 *
 * @param {boolean} isMulti - Whether to allow selecting multiple options, or just a single one (like a radio button). Affects also the return type (Array or single value)
 * @param {Array} [values] - Pass option keys here (same thing that getOptionKey returns). Must be an array. First element will be used if isMulti=false.
 * @param {Array} options - The options to display.
 * @param {function} [getOptionKey] - Function that extracts the key from a passed option.
 * @param {function} [getOptionPrefix] - Function that extracts the prefix from a passed option.
 * @param {function} [getOptionValue] - Function that extracts the value from a passed option.
 * @param {function} [getOptionSuffix] - Function that extracts the suffix from a passed option.
 * @param {function} onChange - Triggered when an option is selected. Always returns an array (but it will contain only one value if isMulti=false).
 * @param {any} customError - A custom Error that is shown above all options
 * @param {string} validationErrorMessageId - The Message ID for the Alert, is there is an Validation Error
 * @param {string} createNewOptionMessageId - The Message ID for the new option row.
 * @param {function} handleCreateNewOption - If set, another option will be displayed saying <createNewOptionMessageId>. On click, this function will be executed.
 * @param {boolean} required - If set, at least one selection is mandatory.
 * @param {string} [dataTestId] - The Test ID to be passed to the hidden value field.
 * @param {string} [propertyName] - The name of the Property (will be used for name in Form). This is required if you want a Form Validation! And this Component needs to be wrapped in a AvForm!!!
 * @param {...Object} props - Other properties to pass to the component.
 *
 * @returns {ReactElement} The rendered OptionsList component.
 */
const OptionsList = ({
  isMulti,
  values,
  options,
  getOptionKey = (key) => key,
  getOptionPrefix,
  getOptionValue = (value) => value,
  getOptionSuffix,
  getOptionDisabled,
  getOptionDisabledReason,
  onChange,
  customError,
  validationErrorMessageId,
  createNewOptionMessageId,
  handleCreateNewOption,
  required,
  dataTestId,
  propertyName,
  ...props
}) => {
  const [selectedOptionKeys, setSelectedOptionKeys] = useState(null);
  const inputFieldRef = useRef();
  const inputFieldFormCtrl = inputFieldRef?.current?.context?.FormCtrl;
  const hasError =
    inputFieldFormCtrl?.isTouched(propertyName) &&
    inputFieldFormCtrl?.hasError(propertyName);

  // set the selectedOptionKeys when values change
  useEffect(() => {
    if (Array.isArray(values)) {
      setSelectedOptionKeys((isMulti ? values : [values?.[0]]) || null);
    }
  }, [values]);

  return (
    <div {...props}>
      {customError}
      {hasError && validationErrorMessageId && (
        <Alert className="d-flex p-50 mb-0 mt-25" color="warning">
          <span className="font-small-3">
            <FormattedMessage id={validationErrorMessageId} />
          </span>
        </Alert>
      )}
      {options?.map((option, index) => {
        const key = getOptionKey(option);
        const prefix = getOptionPrefix && getOptionPrefix(option);
        const value = getOptionValue(option);
        const suffix = getOptionSuffix && getOptionSuffix(option);
        const isDisabled = !!(getOptionDisabled && getOptionDisabled(option));
        const disabledReason =
          isDisabled && getOptionDisabledReason
            ? getOptionDisabledReason(option)
            : null;
        const isSelected = !!selectedOptionKeys?.some(
          (selectedOptionKey) => key === selectedOptionKey,
        );

        return (
          <div key={index}>
          <div
            data-testid="testOption"
            style={{
              transition: 'border 300ms',
              height: '44px',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
            }}
            className={classnames(
              'd-flex align-items-center my-50 px-75 py-50 rounded',
              { 'border-primary': isSelected && !isDisabled },
              { border: (!isSelected || isDisabled) && !hasError },
              { 'border-danger': hasError },
              { 'opacity-50': isDisabled },
            )}
            onClick={() => {
              if (isDisabled) return;
              let updatedSelectedOptionKeys = [];

              if (isSelected && (!required || selectedOptionKeys?.length > 1)) {
                updatedSelectedOptionKeys = isMulti
                  ? selectedOptionKeys.filter(
                      (selectedOptionKey) => key !== selectedOptionKey,
                    )
                  : [];
              } else if (!isSelected) {
                updatedSelectedOptionKeys = isMulti
                  ? [...(selectedOptionKeys || []), key]
                  : [key];
              } else {
                return;
              }

              setSelectedOptionKeys(updatedSelectedOptionKeys);

              if (onChange)
                onChange(
                  isMulti
                    ? updatedSelectedOptionKeys
                    : updatedSelectedOptionKeys?.[0],
                );
            }}
          >
            <div className="d-flex align-items-center flex-grow-0">
              {isSelected ? (
                <Icon.CheckSquare className="vx-icon primary" size={24} />
              ) : (
                <Icon.Square className="vx-icon" size={24} />
              )}
            </div>
            {prefix && (
              <div className="flex-grow-0 pl-75 font-medium-1 line-height-125">
                {prefix}
              </div>
            )}
            <div
              className={classnames(
                'flex-grow-1 font-medium-1 line-height-125 truncate',
                { 'px-75': !!suffix },
                { 'pl-75': !suffix },
              )}
            >
              <Marquee>{value}</Marquee>
            </div>
            {suffix && (
              <div className="flex-grow-0 pl-75 font-medium-1 line-height-125">
                {suffix}
              </div>
            )}
          </div>
          {disabledReason && (
            <div className="font-small-2 text-warning px-75 mb-50 line-height-125">
              {disabledReason}
            </div>
          )}
          </div>
        );
      })}
      {createNewOptionMessageId && handleCreateNewOption && (
        <div
          style={{ transition: 'border 300ms', height: '44px' }}
          className={classnames(
            'd-flex align-items-center my-50 px-75 py-50 dashed rounded',
            { border: !hasError },
            { 'border-danger': hasError },
          )}
          onClick={() => handleCreateNewOption()}
        >
          <div className="d-flex align-items-center">
            <Icon.Plus size={20} className="ml-25 mr-1" />
            <span>
              <FormattedMessage id={createNewOptionMessageId} />
            </span>
          </div>
        </div>
      )}
      {propertyName && (
        <AvInput
          ref={inputFieldRef}
          data-testid={dataTestId}
          type="text"
          name={propertyName}
          value={
            isMulti
              ? JSON.stringify(selectedOptionKeys)
              : selectedOptionKeys?.[0]
          }
          hidden
          required={required}
        />
      )}
    </div>
  );
};

OptionsList.propTypes = {
  isMulti: PropTypes.bool.isRequired,
  values: PropTypes.array,
  options: PropTypes.array.isRequired,
  getOptionKey: PropTypes.func,
  getOptionPrefix: PropTypes.func,
  getOptionValue: PropTypes.func,
  getOptionSuffix: PropTypes.func,
  getOptionDisabled: PropTypes.func,
  getOptionDisabledReason: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  customError: PropTypes.any,
  validationErrorMessageId: PropTypes.string,
  createNewOptionMessageId: PropTypes.string,
  handleCreateNewOption: PropTypes.func,
  dataTestId: PropTypes.string,
  propertyName: PropTypes.string,
  required: PropTypes.bool,
};

export default OptionsList;
